import type { FC } from 'react'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, ArrowUp, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'
import { formatRange } from '@/lib/hours'
import { useBakeryClock } from '@/hooks/use-bakery-clock'
import { OpenStatus } from '@/components/generals/open-status'
import { NewsletterForm } from '@/components/generals/newsletter-form'

import {
    NAV_LINKS,
    OPENING_HOURS,
    SHELL,
    bakery,
    siteConfig
} from '@/constants/site'

const linkClasses = 'link-underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-wash'

// The week as a bakery chalkboard: day, dotted leader, hours. Today is marked once
// the bakery's own date is known, so the server markup never depends on the time.
const WeekHours: FC = () => {
    const now = useBakeryClock()

    return (
        <ul className='mt-5 space-y-2.5 text-[0.9375rem]'>
            {OPENING_HOURS.map(day => {
                const isToday = now?.weekday === day.day

                return (
                    <li key={day.day} className='flex items-baseline gap-3' aria-current={isToday ? 'date' : undefined}>
                        <span className={cn('w-24 shrink-0 transition-colors duration-500', isToday ? 'font-medium text-flour' : 'text-flour/75')}>
                            {day.name}
                        </span>

                        <span className={cn('h-px flex-1 border-b border-dotted', isToday ? 'border-amber-wash/60' : 'border-flour/20')} aria-hidden='true' />

                        <span className={cn('tabular-nums transition-colors duration-500', !day.open ? 'text-flour/40' : isToday ? 'font-medium text-amber-wash' : 'text-flour/75')}>
                            {formatRange(day, true)}
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}

export const Footer: FC = () => {
    const backToTop = (): void => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden bg-crust text-flour'>
            <div className={cn(SHELL, 'pt-20 lg:pt-28')}>
                <div className='grid gap-12 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <div className='lg:col-span-8'>
                        <p className='label-caps text-amber-wash/80'>
                            هر صبح، تازه از تنور
                        </p>

                        <p className='mt-6 max-w-[17ch] font-heading text-[clamp(2.5rem,5.4vw,4.75rem)] leading-[1.25] font-normal tracking-[-0.01em]'>
                            زود بیایید. <span className='text-amber-wash italic'>کروسان‌ها اول از همه تمام می‌شوند.</span>
                        </p>
                    </div>

                    <div className='flex flex-col items-start gap-7 lg:col-span-4 lg:items-end lg:text-end'>
                        <OpenStatus className='text-[0.9375rem] lg:justify-end' detailClassName='text-flour/60' />

                        <div className='flex flex-wrap gap-3 lg:justify-end'>
                            <Link
                                href='/order'
                                className='group relative isolate inline-flex h-12 items-center gap-2 overflow-hidden bg-flour px-6 text-sm font-medium text-crust transition-colors duration-500 before:absolute before:inset-0 before:-z-10 before:origin-left rtl:before:origin-right before:scale-x-0 before:bg-amber-deep before:transition-transform before:duration-500 before:ease-(--ease-soft) hover:text-flour hover:before:scale-x-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-wash'
                            >
                                سفارش برای تحویل حضوری
                                <ArrowRight className='size-4 transition-transform duration-500 ease-(--ease-soft) rtl:-scale-x-100 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' aria-hidden='true' />
                            </Link>

                            <a
                                href={bakery.directionsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='relative isolate inline-flex h-12 items-center gap-2 overflow-hidden border border-flour/50 px-6 text-sm font-medium transition-colors duration-500 before:absolute before:inset-0 before:-z-10 before:origin-left rtl:before:origin-right before:scale-x-0 before:bg-flour before:transition-transform before:duration-500 before:ease-(--ease-soft) hover:text-crust hover:before:scale-x-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-wash'
                            >
                                مسیریابی
                                <ArrowUpRight className='size-4 rtl:-scale-x-100' aria-hidden='true' />
                            </a>
                        </div>
                    </div>
                </div>

                <div className='mt-20 grid gap-12 border-t border-flour/15 pt-12 sm:grid-cols-2 lg:mt-24 lg:grid-cols-12 lg:gap-10 lg:pt-14'>
                    <div className='lg:col-span-4'>
                        <h2 className='label-caps text-flour/55'>ساعات کاری</h2>
                        <WeekHours />
                    </div>

                    <div className='space-y-10 lg:col-span-3 lg:col-start-6'>
                        <div>
                            <h2 className='label-caps text-flour/55'>نشانی ما</h2>

                            <address className='mt-5 text-[0.9375rem] leading-relaxed not-italic'>
                                <span dir='ltr'>{bakery.street}</span>
                                <br />
                                <span dir='ltr'>{bakery.locality}, {bakery.region} {bakery.postalCode}</span>
                            </address>

                            <p className='mt-1 text-sm text-flour/55'>{bakery.nearby}</p>
                        </div>

                        <div>
                            <h2 className='label-caps text-flour/55'>در تماس باشیم</h2>

                            <ul className='mt-5 space-y-2 text-[0.9375rem]'>
                                <li>
                                    <a href={bakery.phoneHref} className={linkClasses} dir='ltr'>{bakery.phone}</a>
                                </li>
                                <li>
                                    <a href={`mailto:${bakery.email}`} className={cn(linkClasses, 'break-all')} dir='ltr'>{bakery.email}</a>
                                </li>
                                <li>
                                    <a href={bakery.instagram.url} target='_blank' rel='noopener noreferrer' className={linkClasses}>
                                        اینستاگرام <span dir='ltr'>{bakery.instagram.handle}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className='sm:col-span-2 lg:col-span-4 lg:col-start-9'>
                        <NewsletterForm dark />
                    </div>
                </div>

                <div className='relative mt-20 flex items-end justify-between gap-5 lg:mt-28' aria-hidden='true'>
                    <p lang='fr' dir='ltr' className='font-heading text-[clamp(3rem,18vw,17rem)] leading-[0.8] font-normal tracking-[-0.02em] whitespace-nowrap select-none md:text-[clamp(3rem,15vw,15rem)]'>
                        La <span className='text-amber-wash italic'>Douceur</span>
                    </p>

                    <Image
                        src='/logo.png'
                        alt=''
                        width={128}
                        height={128}
                        className='mb-[0.6vw] hidden size-[clamp(4.5rem,8vw,8rem)] shrink-0 rounded-full md:block'
                    />
                </div>

                <div className='mt-10 flex flex-col gap-6 border-t border-flour/15 py-7 text-sm lg:flex-row lg:items-center lg:justify-between'>
                    <nav aria-label='پیوندهای پایین صفحه'>
                        <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                            <li>
                                <Link href='/' className={linkClasses}>خانه</Link>
                            </li>

                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className={linkClasses}>{link.label}</Link>
                                </li>
                            ))}

                            <li>
                                <Link href='/visit#contact' className={linkClasses}>تماس با ما</Link>
                            </li>
                        </ul>
                    </nav>

                    <div className='flex flex-wrap items-center gap-x-6 gap-y-3 text-flour/55'>
                        <p>
                            © <span suppressHydrationWarning>{toFaDigits(new Date().getFullYear())}</span> {siteConfig.name}، {bakery.neighborhood}. هزینه‌ی پیش‌سفارش هنگام تحویل پرداخت می‌شود.
                        </p>

                        <button
                            type='button'
                            onClick={backToTop}
                            className='group inline-flex cursor-pointer items-center gap-1.5 text-flour transition-colors duration-300 hover:text-amber-wash focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-wash'
                        >
                            بازگشت به بالا
                            <ArrowUp className='size-4 transition-transform duration-500 ease-(--ease-soft) group-hover:-translate-y-0.5' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    )
}
